<template>
  <div class="pa-3 ma-3">
    <v-alert-dialog ref="alert" />

    <!-- Crear Solicitud -->
    <v-dialog v-model="dialog_add" max-width="720px" persistent>
      <v-card>
        <v-card-title class="headline">Crear Solicitud de Vacante</v-card-title>
        <v-card-text>
          <v-form ref="form_add" v-model="form_valid" lazy-validation>
            <v-row>
              <v-col cols="12" md="8">
                <v-select
                  v-model="edited_item.id_puesto"
                  :items="puestos"
                  item-text="nombre"
                  item-value="id_puesto"
                  :loading="loading_puestos"
                  :disabled="loading_puestos"
                  label="Puesto"
                  outlined
                  dense
                  hide-details
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="edited_item.num_solicitados"
                  label="Personas requeridas"
                  outlined
                  type="number"
                  min="1"
                  dense
                  hide-details
                  :rules="[rules.required, rules.min1, rules.max999, rules.integer]"
                />
              </v-col>

              <!-- NUEVO: Monto salarial (A/B/C) -->
              <v-col cols="12" md="12">
                <v-select
                  v-model="edited_item.salario_nivel"
                  :items="salarios"
                  :item-text="salario_text"
                  item-value="nivel"
                  label="Monto salarial (escala)"
                  outlined
                  dense
                  hide-details
                  :rules="[rules.required]"
                />
              </v-col>

              <!-- NUEVO: Requisitos -->
              <v-col cols="12">
                <v-textarea
                  v-model="edited_item.requisitos"
                  label="Requisitos"
                  auto-grow
                  :rows="1"
                  outlined
                  dense
                  :rules="[rules.required, v => rules.max_len(v, 4000)]"
                  counter="4000"
                  hint="Este texto se mostrará tal cual en los lugares donde se postule la vacante."
                  persistent-hint
                />
              </v-col>

              <!-- Comentario / Justificación (existente) -->
              <v-col cols="12">
                <v-textarea
                  v-model="edited_item.comentario"
                  label="Comentario / Justificación"
                  auto-grow
                  :rows="1"
                  outlined
                  dense
                  :rules="[rules.required, v => rules.max_len(v, 1000)]"
                  counter="1000"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="green" text :disabled="loading_save" @click="close_dialog_add">Cancelar</v-btn>
          <v-btn color="red" text :loading="loading_save" @click="save_dialog_add">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmar Cancelación -->
    <v-dialog v-model="dialog_cancel" max-width="560px" persistent>
      <v-card>
        <v-card-title class="headline">Cancelar Solicitud</v-card-title>
        <v-card-text>
          ¿Desea cancelar la solicitud del puesto
          <strong>“{{ edited_item.puesto_nombre }}”</strong>
          ({{ edited_item.num_solicitados }} persona(s))?
          <br>Esta acción es permanente y no se puede revertir.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="green" text :disabled="loading_cancel" @click="close_dialog_cancel">Cancelar</v-btn>
          <v-btn color="red" text :loading="loading_cancel" @click="save_dialog_cancel">Aceptar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detalles -->
    <v-dialog v-model="dialog_detail" max-width="860px" persistent>
      <v-card>
        <v-card-title class="headline">Detalles de Solicitud</v-card-title>
        <v-card-text>
          <v-skeleton-loader v-if="loading_detail" type="article, table" />
          <div v-else>
            <v-row dense>
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Folio Solicitud</div>
                <div class="font-weight-medium">{{ detail_item.id_solicitud }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Fecha</div>
                <div class="font-weight-medium">{{ format_date(detail_item.created_at) }}</div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Puesto</div>
                <div class="font-weight-medium">{{ detail_item.puesto_nombre }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Estado</div>
                <div class="d-flex align-center">
                  <v-icon :color="state_color(detail_item.estado)" small class="mr-2">{{ state_icon(detail_item.estado) }}</v-icon>
                  <span class="font-weight-medium">{{ detail_item.estado }}</span>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Personas solicitadas</div>
                <div class="font-weight-medium">{{ detail_item.num_solicitados }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Progreso</div>
                <div class="font-weight-medium">
                  <span v-if="show_progress(detail_item.estado)">
                    {{ detail_item.num_contratados || 0 }} de {{ detail_item.num_solicitados }}
                  </span>
                  <span v-else>—</span>
                </div>
              </v-col>

              <!-- Salario -->
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Salario</div>
                <div class="font-weight-medium">
                  {{ (detail_item.salario_nivel || '—') }} — {{ format_currency(detail_item.salario_monto) }}
                </div>
              </v-col>
            </v-row>
            
            <br>
            
            <!-- Expansion panels: Requisitos y Comentario -->
            <v-expansion-panels multiple>
              <v-expansion-panel>
                <v-expansion-panel-header>Requisitos</v-expansion-panel-header>
                <v-expansion-panel-content>
                  {{ detail_item.requisitos || '—' }}
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel>
                <v-expansion-panel-header>Comentario / Justificación</v-expansion-panel-header>
                <v-expansion-panel-content>
                  {{ detail_item.comentario || '—' }}
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Bloque de rechazo visible en Rechazada -->
            <template v-if="detail_item.estado === estados.rechazada">
              <br>
              <v-row>
                <v-col cols="12">
                  <div class="subtitle-2 mb-2">Motivo del rechazo</div>
                  <div class="mb-2">
                    <v-icon small :color="state_color('Rechazada')" class="mr-1">mdi-close-circle</v-icon>
                    <span class="font-weight-medium">{{ detail_item.motivo_rechazo || '—' }}</span>
                  </div>
                  <div class="grey--text">
                    Rechazado por: <strong>{{ detail_item.rechazado_por || '—' }}</strong>
                    &nbsp;|&nbsp;
                    Fecha: <strong>{{ format_date(detail_item.rechazado_en) }}</strong>
                  </div>
                </v-col>
              </v-row>
            </template>

            <!-- Postulantes -->
            <div v-if="detail_item.estado == this.estados.rechazada" class="grey--text text--darken-1">
            </div>
            <div v-else-if="(detail_item.postulantes || []).length">
              <br>
              <div class="subtitle-2 mb-2">Postulantes</div>
              <v-data-table
                :items="detail_item.postulantes"
                :headers="postulantes_headers"
                dense
                disable-pagination
                hide-default-footer
              >
                <template v-slot:[`item.fecha_postulacion`]="{ item }">
                  {{ format_date(item.fecha_postulacion) }}
                </template>
                <template v-slot:[`item.estado`]="{ item }">
                  <div class="align-center">
                    <v-icon :color="postulante_color(item.estado)" small class="mr-2">
                      {{ postulante_icon(item.estado) }}
                    </v-icon>
                    <span>{{ item.estado }}</span>
                  </div>
                </template>
              </v-data-table>
            </div>
            <div v-else class="grey--text text--darken-1">
              No hay postulantes registrados todavía.
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="dialog_detail = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Botón crear -->
    <v-row>
      <v-col cols="12">
        <v-btn color="primary" @click="open_dialog_add">Solicitar Vacante</v-btn>
      </v-col>
    </v-row>

    <!-- Tabla -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :items="items"
          :headers="headers"
          :loading="loading"
          :items-per-page="10"
          :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
          class="elevation-1"
        >
          <template v-slot:[`item.created_at`]="{ item }">
            {{ format_date(item.created_at) }}
          </template>

          <template v-slot:[`item.estado`]="{ item }">
            <div class="d-flex align-center justify-center">
              <v-tooltip bottom color="black">
                <template v-slot:activator="{ on }">
                  <div v-on="on" class="d-flex align-center">
                    <v-icon :color="state_color(item.estado)" small class="mr-2">
                      {{ state_icon(item.estado) }}
                    </v-icon>
                    <span>{{ item.estado }}</span>
                  </div>
                </template>
                <span>{{ state_tooltip(item.estado) }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:[`item.progreso`]="{ item }">
            <span v-if="show_progress(item.estado)">
              {{ item.num_contratados || 0 }} de {{ item.num_solicitados }}
            </span>
            <span v-else>—</span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center justify-center">
              <template v-if="item.estado === estados.pendiente">
                <v-icon small class="mr-2" @click="open_dialog_cancel(item)" title="Cancelar">mdi-cancel</v-icon>
              </template>

              <template v-else-if="show_detail_action(item.estado)">
                <v-icon small class="mr-2" @click="open_dialog_detail(item)" title="Ver detalles">mdi-eye</v-icon>
              </template>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="pa-6 text-center">
              <div class="subtitle-1 mb-2">Sin solicitudes aún</div>
              <v-btn color="primary" @click="open_dialog_add">Crear primera solicitud</v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios'
import VAlertDialog from '@/components/VAlertDialog.vue';

export default {
  name: 'PanelSolicitudesVacantes',
  components: { VAlertDialog },
  data: () => ({
    // endpoints
    api_listar: '/Vacantes/Solicitudes',          // GET
    api_crear: '/Vacantes/Solicitudes',           // POST
    api_detalle_base: '/Vacantes/Solicitudes',    // GET /{id}
    api_cancelar_base: '/Vacantes/Solicitudes',   // DELETE /{id}
    api_puestos: '/RecursosHumanos/ListarPuestos',// GET

    // ui state
    dialog_add: false,
    dialog_cancel: false,
    dialog_detail: false,

    loading: false,
    loading_save: false,
    loading_cancel: false,
    loading_detail: false,
    loading_puestos: false,

    form_valid: false,

    // data
    items: [],
    puestos: [],

    headers: [
      { text: 'Folio', value: 'id_solicitud', align: 'center', width: '10%', sortable: true },
      { text: 'Fecha', value: 'created_at', align: 'center', width: '15%', sortable: true },
      { text: 'Puesto', value: 'puesto_nombre', align: 'center', width: '20%', sortable: true },
      { text: '# Personas Solicitadas', value: 'num_solicitados', align: 'center', width: '14%', sortable: true },
      { text: 'Estado', value: 'estado', align: 'center', width: '16%', sortable: true },
      { text: 'Progreso', value: 'progreso', align: 'center', width: '15%', sortable: false },
      { text: 'Acciones', value: 'actions', align: 'center', width: '10%', sortable: false },
    ],

    // escalas salariales (ejemplo; ajusta montos a tu realidad)
    salarios: [
      { nivel: 'A', monto: 12000 },
      { nivel: 'B', monto: 16000 },
      { nivel: 'C', monto: 22000 },
    ],

    edited_item: {
      id_puesto: null,
      num_solicitados: null,
      salario_nivel: null,   // NUEVO
      requisitos: '',        // NUEVO
      comentario: '',
      id_solicitud: null,
      puesto_nombre: null,
    },
    default_edited_item: {
      id_puesto: null,
      num_solicitados: null,
      salario_nivel: null,
      requisitos: '',
      comentario: '',
      id_solicitud: null,
      puesto_nombre: null,
    },

    detail_item: {},

    estados: {
      cancelada: 'Cancelada',
      rechazada: 'Rechazada',
      aprobado: 'Aprobado',
      reclutamiento: 'En Reclutamiento',
      finzalizado: 'Finzalizado', // (intencional)
      pendiente: 'Pendiente de Autorizacion',
    },

    // Reglas
    rules: {
      required: v => (v !== null && v !== undefined && String(v).trim().length > 0) || 'Campo requerido',
      min1: v => (Number(v) >= 1) || 'Debe ser al menos 1',
      max999: v => (Number(v) <= 999) || 'Máximo 999',
      integer: v => Number.isInteger(Number(v)) || 'Debe ser entero',
      max_len: (v, n) => (!v || String(v).length <= n) || `Máximo ${n} caracteres`,
    },

    postulantes_headers: [
      { text: 'Nombre', value: 'nombre', align: 'left' },
      { text: 'Correo', value: 'correo', align: 'left' },
      { text: 'Estado', value: 'estado', align: 'center' },
      { text: 'Fecha Postulación', value: 'fecha_postulacion', align: 'center' },
    ],
  }),

  computed: {
    salario_monto_formateado() {
      const item = this.salarios.find(s => s.nivel === this.edited_item.salario_nivel);
      return this.format_currency(item?.monto);
    }
  },

  mounted() {
    this.get_puestos();
    this.get_items();
  },

  methods: {
    // helpers
    format_date(val) {
      if (!val) return '—';
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        return d.toLocaleString();
      } catch { return String(val); }
    },
    format_currency(n) {
      if (n === null || n === undefined || isNaN(Number(n))) return '—';
      return Number(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
    },
    salario_text(item) {
      return `${item.nivel} — ${this.format_currency(item.monto)}`;
    },

    show_progress(estado) {
      return [this.estados.aprobado, this.estados.reclutamiento, this.estados.finzalizado].includes(estado);
    },

    show_detail_action(estado) {
      return [this.estados.aprobado, this.estados.reclutamiento, this.estados.finzalizado, this.estados.rechazada].includes(estado);
    },

    state_icon(estado) {
      const e = (estado || '').toLowerCase();
      if (e.includes('pendiente')) return 'mdi-timer-sand';
      if (e.includes('aprob')) return 'mdi-check-circle';
      if (e.includes('recluta')) return 'mdi-account-search';
      if (e.includes('finza') || e.includes('final')) return 'mdi-flag-checkered';
      if (e.includes('rechaz')) return 'mdi-close-circle';
      if (e.includes('cancel')) return 'mdi-cancel';
      return 'mdi-help-circle';
    },

    state_color(estado) {
      const e = (estado || '').toLowerCase();
      if (e.includes('pendiente')) return 'orange darken-2';
      if (e.includes('aprob')) return 'green';
      if (e.includes('recluta')) return 'indigo';
      if (e.includes('finza') || e.includes('final')) return 'teal';
      if (e.includes('rechaz')) return 'red darken-1';
      if (e.includes('cancel')) return 'grey';
      return 'blue-grey';
    },

    // Estados de postulantes (extendidos)
    postulante_icon(e) {
    const normalize = s =>
      (s || '')
        .toString()
        .trim()
        .toLowerCase();

    const v = normalize(e);

    // Mapeo exacto por texto completo
    const icon_map = {
      'en espera de documentacion': 'mdi-file-clock',
      'rechazado - post revision de documentacion': 'mdi-cancel',
      'en entrevista - rrh': 'mdi-account-tie',
      'rechazado - post entrevista rrhh': 'mdi-cancel',
      'en entrevista - por solicitante': 'mdi-account-voice',
      'rechazado - post entrevista solicitante': 'mdi-cancel',
      'contratado': 'mdi-account-check',
    };

    if (icon_map[v]) return icon_map[v];

    // Fallbacks (opcional, por si llegan estados genéricos)
    if (v.includes('rechaz')) return 'mdi-cancel';
    if (v.includes('entrev')) return 'mdi-account-search';
    return 'mdi-account';
  },

    postulante_color(e) {
      const normalize = s =>
        (s || '')
          .toString()
          .trim()
          .toLowerCase();

      const v = normalize(e);

      // Mapeo exacto por texto completo
      const color_map = {
        'en espera de documentacion': 'blue-grey',
        'rechazado - post revision de documentacion': 'red darken-1',
        'en entrevista - rrh': 'indigo',
        'rechazado - post entrevista rrhh': 'red darken-1',
        'en entrevista - por solicitante': 'purple',
        'rechazado - post entrevista solicitante': 'red darken-1',
        'contratado': 'green',
      };

      if (color_map[v]) return color_map[v];

      // Fallbacks (opcional, por si llegan estados genéricos)
      if (v.includes('rechaz')) return 'red darken-1';
      if (v.includes('entrev')) return 'indigo';
      return 'blue-grey';
    },


    state_tooltip(estado) {
      const e = (estado || '').toLowerCase();
      if (e.includes('pendiente'))
        return 'La solicitud fue enviada y está esperando autorización de Recursos Humanos.';
      if (e.includes('aprobado'))
        return 'La solicitud fue aprobada y está lista para iniciar el reclutamiento.';
      if (e.includes('recluta'))
        return 'El proceso de reclutamiento está en curso; ya hay postulantes.';
      if (e.includes('finza') || e.includes('final'))
        return 'La vacante ha sido cubierta completamente.';
      if (e.includes('rechaz'))
        return 'La solicitud fue rechazada; consulta los detalles para conocer el motivo.';
      if (e.includes('cancel'))
        return 'La solicitud fue cancelada por el solicitante antes de su aprobación.';
      return 'Estado desconocido.';
    },

    toast_error(msg) {
      this.$refs.alert.setMessage('', msg || 'Ocurrió un error inesperado');
      this.$refs.alert.open();
    },

    // catálogo
    get_puestos(){
      this.loading_puestos = true;
      axios.get(this.api_puestos)
        .then(res => { this.puestos = Array.isArray(res.data) ? res.data : []; })
        .catch(() => { this.toast_error('No se pudo cargar el catálogo de puestos.'); this.puestos = []; })
        .finally(() => { this.loading_puestos = false; });
    },

    // listado
    get_items() {
      this.loading = true;
      axios.get(this.api_listar)
        .then(res => {
          const data = Array.isArray(res.data) ? res.data : [];
          this.items = data.length ? this.normalize_list(data) : this.mock_examples();
        })
        .catch(() => { this.items = this.mock_examples(); })
        .finally(() => { this.loading = false; });
    },

    // normaliza (por si la API trae camelCase)
    normalize_list(list) {
      return list.map(x => ({
        id_solicitud: x.id_solicitud ?? x.idSolicitud ?? x.id,
        created_at: x.created_at ?? x.createdAt,
        id_puesto: x.id_puesto ?? x.idPuesto,
        puesto_nombre: x.puesto_nombre ?? x.puestoNombre ?? x.puesto,
        num_solicitados: x.num_solicitados ?? x.numSolicitados,
        num_contratados: x.num_contratados ?? x.numContratados,
        comentario: x.comentario,
        requisitos: x.requisitos,
        salario_nivel: x.salario_nivel ?? x.salarioNivel,
        salario_monto: x.salario_monto ?? x.salarioMonto,
        estado: x.estado,
        motivo_rechazo: x.motivo_rechazo ?? x.motivoRechazo,
        rechazado_por: x.rechazado_por ?? x.rechazadoPor,
        rechazado_en: x.rechazado_en ?? x.rechazadoEn,
        postulantes: x.postulantes
      }));
    },

    // crear
    open_dialog_add() {
      this.edited_item = Object.assign({}, this.default_edited_item);
      this.dialog_add = true;
      this.$nextTick(() => this.$refs.form_add && this.$refs.form_add.resetValidation());
    },
    close_dialog_add() {
      this.edited_item = Object.assign({}, this.default_edited_item);
      this.dialog_add = false;
    },
    save_dialog_add() {
      if (!(this.$refs.form_add && this.$refs.form_add.validate())) return;
      if (!this.edited_item.id_puesto) { this.toast_error('Debe seleccionar un puesto.'); return; }

      this.loading_save = true;

      const sel = this.salarios.find(s => s.nivel === this.edited_item.salario_nivel);
      const payload = {
        id_puesto: this.edited_item.id_puesto,
        num_solicitados: Number(this.edited_item.num_solicitados),
        salario_nivel: this.edited_item.salario_nivel,           // NUEVO
        salario_monto: sel?.monto ?? null,                       // NUEVO
        requisitos: this.edited_item.requisitos,                 // NUEVO
        comentario: this.edited_item.comentario
      };

      axios.post(this.api_crear, payload)
        .then(() => { this.get_items(); this.close_dialog_add(); })
        .catch(err => {
          if (err?.response?.status === 409) this.toast_error('Existe una solicitud idéntica pendiente.');
          else this.toast_error('No se pudo crear la solicitud.');
        })
        .finally(() => { this.loading_save = false; });
    },

    // cancelar (solo pendiente)
    open_dialog_cancel(item) {
      this.edited_item = {
        id_solicitud: item.id_solicitud,
        puesto_nombre: item.puesto_nombre,
        num_solicitados: item.num_solicitados
      };
      this.dialog_cancel = true;
    },
    close_dialog_cancel() {
      this.edited_item = Object.assign({}, this.default_edited_item);
      this.dialog_cancel = false;
    },
    save_dialog_cancel() {
      this.loading_cancel = true;
      axios.delete(`${this.api_cancelar_base}/${this.edited_item.id_solicitud}`)
        .then(() => { this.close_dialog_cancel(); this.get_items(); })
        .catch(() => { this.toast_error('No se pudo cancelar la solicitud.'); })
        .finally(() => { this.loading_cancel = false; });
    },

    // detalles (aprobado, reclutamiento, finzalizado, rechazada)
    open_dialog_detail(item) {
      this.dialog_detail = true;
      this.loading_detail = true;
      this.detail_item = {};

      axios.get(`${this.api_detalle_base}/${item.id_solicitud}`)
        .then(res => { this.detail_item = this.normalize_detail(res.data || {}, item); })
        .catch(() => { this.detail_item = this.normalize_detail({}, item); })
        .finally(() => { this.loading_detail = false; });
    },

    normalize_detail(api, base) {
      return {
        id_solicitud: api.id_solicitud ?? api.idSolicitud ?? base.id_solicitud,
        created_at: api.created_at ?? api.createdAt ?? base.created_at,
        puesto_nombre: api.puesto_nombre ?? api.puestoNombre ?? base.puesto_nombre,
        num_solicitados: api.num_solicitados ?? api.numSolicitados ?? base.num_solicitados,
        num_contratados: api.num_contratados ?? api.numContratados ?? base.num_contratados ?? 0,
        comentario: api.comentario ?? base.comentario ?? '',
        requisitos: api.requisitos ?? base.requisitos ?? '',
        salario_nivel: api.salario_nivel ?? api.salarioNivel ?? base.salario_nivel ?? null,
        salario_monto: api.salario_monto ?? api.salarioMonto ?? base.salario_monto ?? null,
        estado: api.estado ?? base.estado,
        motivo_rechazo: api.motivo_rechazo ?? api.motivoRechazo ?? base.motivo_rechazo,
        rechazado_por: api.rechazado_por ?? api.rechazadoPor ?? base.rechazado_por,
        rechazado_en: api.rechazado_en ?? api.rechazadoEn ?? base.rechazado_en,
        postulantes: api.postulantes ?? base.postulantes ?? []
      };
    },

    // ejemplos usando tu catálogo (ampliado con estados de postulantes)
    mock_examples() {
      const now = new Date();
      const iso = (d) => new Date(d).toISOString();

      const postulantes_demo = [
        { nombre: 'María Pérez', correo: 'maria.perez@example.com', estado: 'En espera de documentacion', fecha_postulacion: iso(now - 86400000 * 6.5) },
        { nombre: 'Luis Gómez', correo: 'luis.gomez@example.com', estado: 'Rechazado - Post Revision de Documentacion', fecha_postulacion: iso(now - 86400000 * 6) },
        { nombre: 'Ana Ruiz', correo: 'ana.ruiz@example.com', estado: 'En Entrevista - RRH', fecha_postulacion: iso(now - 86400000 * 5.5) },
        { nombre: 'Carlos Soto', correo: 'carlos.soto@example.com', estado: 'Rechazado - Post Entrevista RRHH', fecha_postulacion: iso(now - 86400000 * 5) },
        { nombre: 'Diana León', correo: 'diana.leon@example.com', estado: 'En Entrevista - Por Solicitante', fecha_postulacion: iso(now - 86400000 * 4.5) },
        { nombre: 'Iván Torres', correo: 'ivan.torres@example.com', estado: 'Rechazado - Post Entrevista Solicitante', fecha_postulacion: iso(now - 86400000 * 4) },
        { nombre: 'Omar Gil', correo: 'omar.gil@example.com', estado: 'Contratado', fecha_postulacion: iso(now - 86400000 * 3.5) },
      ];

      return [
        {
          id_solicitud: 'VAC-2001',
          created_at: iso(now),
          id_puesto: 'PST-01',
          puesto_nombre: 'Auxiliar de recursos humanos',
          num_solicitados: 2,
          num_contratados: 0,
          requisitos: 'Licenciatura en curso. Manejo de Excel. Excelente ortografía.',
          comentario: 'Cobertura por crecimiento en planta.',
          salario_nivel: 'A',
          salario_monto: 12000,
          estado: this.estados.pendiente,
        },
        {
          id_solicitud: 'VAC-2002',
          created_at: iso(now - 86400000 * 2),
          id_puesto: 'PST-02',
          puesto_nombre: 'Médico general',
          num_solicitados: 3,
          num_contratados: 0,
          requisitos: 'Cédula profesional, experiencia en consulta general, disponibilidad de horario.',
          comentario: 'Nueva clínica en operación.',
          salario_nivel: 'B',
          salario_monto: 16000,
          estado: this.estados.aprobado,
          postulantes: postulantes_demo.slice(0, 2)
        },
        {
          id_solicitud: 'VAC-2003',
          created_at: iso(now - 86400000 * 5),
          id_puesto: 'PST-03',
          puesto_nombre: 'Responsable de tecnologías de la información',
          num_solicitados: 2,
          num_contratados: 1,
          requisitos: '5+ años en redes y servidores. Conocimiento de Docker, Proxmox y bases de datos.',
          comentario: 'Proyecto de modernización de infraestructura.',
          salario_nivel: 'C',
          salario_monto: 22000,
          estado: this.estados.reclutamiento,
          postulantes: postulantes_demo.slice(0, 5)
        },
        {
          id_solicitud: 'VAC-2004',
          created_at: iso(now - 86400000 * 9),
          id_puesto: 'PST-04',
          puesto_nombre: 'Dentista',
          num_solicitados: 1,
          num_contratados: 1,
          requisitos: 'Título y cédula, experiencia mínima 2 años, trato amable.',
          comentario: 'Cobertura de turno vespertino.',
          salario_nivel: 'B',
          salario_monto: 16000,
          estado: this.estados.finzalizado,
          postulantes: postulantes_demo.slice(6) // contratado
        },
        {
          id_solicitud: 'VAC-2005',
          created_at: iso(now - 86400000 * 6),
          id_puesto: 'PST-05',
          puesto_nombre: 'Técnico radiólogo',
          num_solicitados: 1,
          num_contratados: 0,
          requisitos: 'Conocimientos en Rayos X y tomografía. Turno nocturno.',
          comentario: 'Sustitución por baja.',
          salario_nivel: 'B',
          salario_monto: 16000,
          estado: this.estados.rechazada,
          motivo_rechazo: 'El presupuesto del área no lo permite en este trimestre.',
          rechazado_por: 'Dirección general',
          rechazado_en: iso(now - 86400000 * 5.5),
          postulantes: []
        },
        {
          id_solicitud: 'VAC-2006',
          created_at: iso(now - 86400000 * 12),
          id_puesto: 'PST-06',
          puesto_nombre: 'Auxiliar de comunicación',
          num_solicitados: 1,
          num_contratados: 0,
          requisitos: 'Edición básica, redacción, redes sociales.',
          comentario: 'Campaña regional.',
          salario_nivel: 'A',
          salario_monto: 12000,
          estado: this.estados.cancelada,
        },
      ];
    },
  }
}
</script>
